// REST calls to the Go auth endpoints.
//
// Server resolution order (first hit wins):
//   1. ?server=https://host:port override in the page URL (then persisted)
//   2. localStorage "hc_server_url" (set by a previous override)
//   3. VITE_SERVER_URL baked at build time
//   4. same-origin fallback (window.location.origin) — this is what makes the
//      cloud topology work with zero config: the Go server serves the PWA
//      dist itself, so API/WS are same-origin and need no setting at all.
import { API } from './protocol';

function resolveServerUrl(): string {
  try {
    const q = new URLSearchParams(window.location.search).get('server');
    if (q) {
      const normalized = q.replace(/\/$/, '');
      try {
        window.localStorage.setItem('hc_server_url', normalized);
      } catch {
        /* private mode */
      }
      return normalized;
    }
    const stored = window.localStorage.getItem('hc_server_url');
    if (stored) return stored.replace(/\/$/, '');
  } catch {
    /* non-browser / opaque origin */
  }
  const baked = import.meta.env.VITE_SERVER_URL as string | undefined;
  if (baked) return baked.replace(/\/$/, '');
  // Same-origin: when served by the Go server (cloud or preview), the page
  // origin IS the API origin. Only fall back to localhost when the page
  // itself is a local dev server.
  try {
    const origin = window.location.origin;
    if (origin && !origin.startsWith('http://localhost')) return origin;
    if (origin && origin !== 'null') return origin;
  } catch {
    /* ignore */
  }
  return 'http://localhost:8080';
}

/** Server origin; overridable via ?server=, localStorage, or VITE_SERVER_URL. */
export const SERVER_URL: string = resolveServerUrl();

/** Shown when GET /pcs (My Drives list) fails. */
export const DRIVES_LIST_ERROR =
  'Could not load your drives.\nPlease check your internet.';

/** Turn fetch/network failures into user-facing text (avoids bare "TypeError: Failed to fetch"). */
export function formatApiError(err: unknown): string {
  if (err instanceof Error) {
    const msg = err.message;
    const networkLike =
      msg === 'Failed to fetch' ||
      msg.includes('NetworkError') ||
      msg.includes('Network request failed') ||
      msg.includes('Load failed');
    if (networkLike) {
      return 'Please check your internet.';
    }
    return msg;
  }
  return String(err);
}

function rethrowFetchError(err: unknown): never {
  if (err instanceof Error && err.message.startsWith('Cannot reach the server')) {
    throw err;
  }
  throw new Error(formatApiError(err));
}

function resolveWsUrl(serverUrl: string): string {
  const explicit = import.meta.env.VITE_WS_URL as string | undefined;
  if (explicit) return explicit;
  const path = API.ws;
  try {
    const page = window.location;
    if (page.protocol === 'https:') {
      const api = new URL(serverUrl);
      const sameHost = api.hostname === page.hostname;
      if (sameHost) {
        return `wss://${page.host}${path}`;
      }
      return `wss://${api.host}${path}`;
    }
  } catch {
    /* non-browser */
  }
  return `${serverUrl.replace(/^http/, 'ws')}${path}`;
}

// Signaling socket URL. Falls back to <origin>/ws — the endpoint the Go
// router registers — so the /ws path is never dropped. Without it the browser
// opens ws://host:8080 (path "/"), which the server answers with 404 and the
// handshake silently fails.
export const WS_URL: string = resolveWsUrl(SERVER_URL);

export interface AuthResponse {
  userId: string;
  token: string;
  username: string;
  email?: string;
  hasEncryptionPassword?: boolean;
  granted?: { pcId: string; folderPath: string; userEmail: string }[];
  /** Owned PCs → effective plan tier from the signaling server. */
  planTiers?: Record<string, 'paid' | 'free'>;
}

/** Registration inputs (item 1: email + optional invite codes; item 2: encryption password). */
export interface RegisterInput {
  email: string;
  password: string;
  /** Client-side encryption password (optional; enables .hcloud vault). */
  encPassword?: string;
  /** Out-of-band access codes that claim pending share invites on registration. */
  inviteCodes?: string[];
  /** From email OTP / magic-link verification before register (invited users). */
  regTicket?: string;
}

/** A PC the caller owns or has a folder shared with (item 6: My PCs picker). */
export interface PCInfo {
  id: string;
  name: string;
  ownerEmail: string;
  ownerUserId: string;
  installedAt: string;
  planTier?: 'paid' | 'free';
  folders: { path: string; name: string }[];
}

/** A pending share invite for the caller (item 1). */
export interface InviteInfo {
  id: string;
  pcId: string;
  pcName: string;
  folder: string;
  inviter: string;
  email: string;
  accepted: boolean;
  createdAt: string;
  expiresAt: string;
}

async function post<T>(
  path: string,
  body: unknown,
  token?: string
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${SERVER_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    rethrowFetchError(e);
  }
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `Request failed (${res.status})`);
  }
  return data;
}

async function get<T>(path: string, token?: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${SERVER_URL}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (e) {
    rethrowFetchError(e);
  }
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `Request failed (${res.status})`);
  }
  return data;
}

export function register(input: RegisterInput): Promise<AuthResponse> {
  return post<AuthResponse>(API.register, {
    email: input.email,
    password: input.password,
    ...(input.encPassword ? { encPassword: input.encPassword } : {}),
    ...(input.inviteCodes && input.inviteCodes.length
      ? { inviteCodes: input.inviteCodes }
      : {}),
    ...(input.regTicket ? { regTicket: input.regTicket } : {}),
  });
}

export function sendRegisterCode(email: string): Promise<{ expiresIn: number }> {
  return post<{ ok: boolean; expiresIn: number }>(API.registerSendCode, { email }).then(
    (d) => ({ expiresIn: d.expiresIn ?? 86400 })
  );
}

export function confirmRegisterCode(
  email: string,
  code: string
): Promise<{ email: string; regTicket: string }> {
  return post<{ email: string; regTicket: string }>(API.registerConfirmCode, {
    email,
    code: code.replace(/\s/g, ''),
  });
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  let res: Response;
  try {
    res = await fetch(`${SERVER_URL}${API.login}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch (e) {
    rethrowFetchError(e);
  }
  const data = (await res.json().catch(() => ({}))) as AuthResponse & { error?: string };
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error('Invalid email or password.');
    }
    throw new Error(data.error ?? `Request failed (${res.status})`);
  }
  return data;
}

/** List the PCs the caller owns or has a folder shared with (item 6). */
export function listPCs(token: string): Promise<PCInfo[]> {
  return get<{ pcs: PCInfo[] }>(API.pcs, token).then((d) => d.pcs ?? []);
}

/** List the caller's pending share invites (item 1). */
export function myInvites(token: string): Promise<InviteInfo[]> {
  return get<{ invites: InviteInfo[] }>(API.invitesMine, token).then(
    (d) => d.invites ?? []
  );
}

/** Claim pending invites with out-of-band access codes (item 1). */
export function claimInvites(token: string, codes: string[]): Promise<unknown> {
  return post(API.claimInvites, { codes }, token);
}

/**
 * Item 2: initialise the account's ONE-TIME encryption password (set-once;
 * the server answers 409 when one already exists — it can never change).
 */
export function setEncPassword(token: string, encPassword: string): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>(API.setEncPassword, { encPassword }, token);
}

/**
 * Item 2: verify a candidate encryption password against the stored Argon2id
 * verifier using this session's bearer token (email implied server-side).
 */
export function verifyEncPassword(token: string, encPassword: string): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>(API.verifyEncPassword, { encPassword }, token);
}
